// ui/src/components/tenant-admin/sections/EarningsModelSelector.jsx

/**
 * @file EarningsModelSelector.jsx
 * @description Plan document section for selecting the primary earnings spine
 *   (e.g. revenue_bookings, transaction_commission, mbo_objective). Renders
 *   a 3-column card grid in edit mode and a read-only display in approved/locked
 *   state. Includes an impact confirmation dialog when changing an already-
 *   configured spine. No API calls — prop-driven. Spine selection is persisted
 *   by the parent plan document save flow (ep 7 POST /api/v1/plans or amendment).
 * @module tenant-admin/sections
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileA_v4_final.md §1.7 — POST /api/v1/plans (ep 7)
 * @see api_spec_fileA_v4_final.md §1.12 — amendments/begin (ep 12)
 */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// S812 D4 — spine metadata for the read-only Economic Model card shown in authoring mode.
// Full 9-card grid is shown ONLY in NewPlanCreationSheet (pre-creation).
// In draft authoring, spine is immutable — compact card replaces the grid.
const SPINES = [
  {
    key: 'revenue_bookings',
    label: 'Revenue / Bookings',
    description:
      'Comp earned as a percentage of quota attainment — revenue, ARR, ACV, TCV, or bookings.',
    explanation: 'Quota attainment drives payout. Rate table required.',
    requiresRateTable: true,
    requiresQuota: true,
  },
  {
    key: 'transaction_commission',
    label: 'Transaction Commission',
    description:
      'Flat rate or percentage paid per deal or unit sold. No quota required.',
    explanation: 'Each transaction triggers a flat or percentage payout. No quota target needed.',
    requiresRateTable: true,
    requiresQuota: false,
  },
  {
    key: 'volume_quantity',
    label: 'Volume / Quantity',
    description:
      'Comp earned based on total units or volume against a target.',
    explanation: 'Volume against a target drives payout. Rate table required.',
    requiresRateTable: true,
    requiresQuota: true,
  },
  {
    key: 'market_share',
    label: 'Market Share / Penetration',
    description:
      'Comp earned based on share of the addressable market versus a target share.',
    explanation: 'Market share versus a target drives payout. Rate table required.',
    requiresRateTable: true,
    requiresQuota: true,
  },
  {
    key: 'grr_nrr_retention',
    label: 'Retention (GRR / NRR)',
    description:
      'Comp earned based on gross or net revenue retained versus a retention target.',
    explanation: 'Retention rate versus a target drives payout. Rate table required.',
    requiresRateTable: true,
    requiresQuota: true,
  },
  {
    key: 'mbo_objective',
    label: 'MBO / Objective',
    description:
      'Comp earned based on achievement of defined milestones, scores, or objectives.',
    explanation: 'Objective achievement drives payout. No rate table required.',
    requiresRateTable: false,
    requiresQuota: false,
  },
  {
    key: 'margin_profit',
    label: 'Margin / Profit',
    description:
      'Comp earned based on gross margin percentage or dollar margin versus target.',
    explanation: 'Margin versus a target drives payout. Rate table required.',
    requiresRateTable: true,
    requiresQuota: true,
  },
  {
    key: 'bonus_pool',
    label: 'Bonus Pool / Allocation',
    description:
      'Comp allocated from a shared pool based on contribution or ranking.',
    explanation: 'Allocation from a shared pool. No rate table or quota required.',
    requiresRateTable: false,
    requiresQuota: false,
  },
  {
    key: 'pharma_script',
    label: 'Pharma / Script Volume',
    description:
      'Comp earned based on prescription script volume versus target.',
    explanation: 'Script volume versus a target drives payout. Rate table required.',
    requiresRateTable: true,
    requiresQuota: true,
  },
];

const findSpine = (key) => SPINES.find((spine) => spine.key === key) ?? null;

/**
 * @typedef {
 *   'revenue_bookings'|
 *   'transaction_commission'|
 *   'volume_quantity'|
 *   'market_share'|
 *   'grr_nrr_retention'|
 *   'mbo_objective'|
 *   'margin_profit'|
 *   'bonus_pool'|
 *   'pharma_script'
 * } EarningsSpine
 */

/**
 * @param {Object} props
 * @param {EarningsSpine|null} props.selectedSpine
 * @param {boolean} props.hasExistingConfig
 * @param {boolean} props.readOnly
 * @param {(spineKey: EarningsSpine) => void} props.onSelect
 * @returns {JSX.Element}
 */
export default function EarningsModelSelector({
  selectedSpine,
  hasExistingConfig,
  readOnly,
  isAuthoring = false,
  onSelect,
}) {
  const [pendingSpine, setPendingSpine] = useState(null);
  const [showImpactConfirmation, setShowImpactConfirmation] =
    useState(false);

  const confirmationRef = useRef(null);

  useEffect(() => {
    if (showImpactConfirmation) {
      confirmationRef.current?.focus();
    }
  }, [showImpactConfirmation]);

  useEffect(() => {
    setPendingSpine(null);
    setShowImpactConfirmation(false);
  }, [selectedSpine, readOnly]);

  const current = findSpine(selectedSpine);
  const pending = findSpine(pendingSpine);

  const requestSelection = (nextKey) => {
    if (nextKey === selectedSpine) {
      return;
    }

    if (hasExistingConfig && selectedSpine) {
      setPendingSpine(nextKey);
      setShowImpactConfirmation(true);
      return;
    }

    onSelect(nextKey);
  };

  const confirmChange = () => {
    if (!pendingSpine) {
      return;
    }

    const next = pendingSpine;
    setPendingSpine(null);
    setShowImpactConfirmation(false);
    onSelect(next);
  };

  const cancelChange = () => {
    setPendingSpine(null);
    setShowImpactConfirmation(false);
  };

  const handleCardKeyDown = (event, key) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      requestSelection(key);
    }
  };

  return (
    <section
      className="modifier-manager-card earnings-model-selector"
      aria-labelledby="earnings-model-heading"
    >
      <style>{`
        .earnings-model-selector {
          background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border));
          color: rgb(var(--color-text));
          font-family: "IBM Plex Sans", sans-serif;
        }

        .earnings-model-selector .modifier-manager-header {
          border-bottom: 1px solid rgb(var(--color-border));
          padding: 16px;
        }

        .earnings-model-selector .modifier-manager-body {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
        }

        .earnings-model-heading {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
        }

        .earnings-model-subtitle {
          color: rgb(var(--color-text-muted));
          font-size: 13px;
          line-height: 1.5;
          margin: 4px 0 0;
        }

        .earnings-model-grid {
          display: grid;
          gap: 10px;
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .earnings-model-card {
          background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border));
          border-radius: 10px;
          color: rgb(var(--color-text));
          cursor: pointer;
          font: inherit;
          min-height: 128px;
          padding: 14px;
          text-align: left;
          width: 100%;
        }

        .earnings-model-card[data-selected="true"] {
          background: rgb(var(--color-selected-bg));
        }

        .earnings-model-card-title {
          display: block;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.4;
        }

        .earnings-model-card-copy {
          color: rgb(var(--color-text-muted));
          display: block;
          font-size: 12px;
          line-height: 1.5;
          margin-top: 6px;
        }

        .earnings-model-confirmation {
          background: rgb(var(--color-warning) / 0.08);
          border: 1px solid rgb(var(--color-border));
          border-radius: 10px;
          padding: 14px;
        }

        .earnings-model-confirmation:focus {
          outline: none;
        }

        .earnings-model-confirmation-title {
          font-size: 14px;
          font-weight: 600;
          margin: 0;
        }

        .earnings-model-confirmation-copy {
          font-size: 13px;
          line-height: 1.5;
          margin: 6px 0 0;
        }

        .earnings-model-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 12px;
        }

        .earnings-model-readonly {
          background: rgb(var(--color-surface-muted));
          border: 1px solid rgb(var(--color-border));
          border-radius: 10px;
          padding: 16px;
        }

        .earnings-model-readonly-title {
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }

        .earnings-model-readonly-copy {
          color: rgb(var(--color-text-muted));
          font-size: 13px;
          line-height: 1.5;
          margin: 6px 0 0;
        }

        .earnings-model-selector .modifier-manager-readonly-note {
          background: rgb(var(--color-surface-muted));
          border: 1px solid rgb(var(--color-border));
          border-radius: 8px;
          color: rgb(var(--color-text-muted));
          font-size: 13px;
          line-height: 1.5;
          margin: 0;
          padding: 10px 12px;
        }

        .earnings-model-economic-card {
          background: rgb(var(--color-surface-muted, var(--color-surface)));
          border: 1px solid rgb(var(--color-border));
          border-radius: 10px;
          padding: 16px;
        }

        .earnings-model-economic-card-label {
          font-size: 13px;
          font-weight: 500;
          color: rgb(var(--color-text-muted));
          margin: 0 0 4px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .earnings-model-economic-card-title {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 6px;
          color: rgb(var(--color-text));
        }

        .earnings-model-economic-card-explanation {
          font-size: 13px;
          color: rgb(var(--color-text-muted));
          margin: 0 0 12px;
          line-height: 1.5;
        }

        .earnings-model-economic-card-meta {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 8px;
        }

        .earnings-model-economic-card-meta-item {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          color: rgb(var(--color-text-muted));
        }

        .earnings-model-economic-card-meta-item--yes {
          color: rgb(var(--color-success, 22 163 74));
        }

        .earnings-model-change-hint {
          font-size: 12px;
          color: rgb(var(--color-text-muted));
          margin-top: 10px;
        }

        .earnings-model-change-hint a,
        .earnings-model-change-hint button.text-button {
          font-size: 12px;
        }
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 620px) {
          .earnings-model-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <header className="modifier-manager-header">
        <h2 id="earnings-model-heading" className="earnings-model-heading">
          Earnings model
        </h2>
        <p className="earnings-model-subtitle">
          How does this plan primarily earn variable compensation?
        </p>
      </header>

      <div className="modifier-manager-body">
        {/* S812 D4 — isAuthoring: spine is set at creation (EP7) and is immutable in authoring.
            Show compact read-only Economic Model card. Full 9-card grid only pre-creation.
            To change the spine: use "Create a plan with a different economic model" in Actions menu
            which launches EP7 with identity values pre-filled. EP8 (Fork) cannot change spine. */}
        {isAuthoring ? (
          <div className="earnings-model-economic-card">
            <p className="earnings-model-economic-card-label">Economic model</p>
            <p className="earnings-model-economic-card-title">
              {current?.label ?? 'No economic model configured'}
            </p>
            {current && (
              <p className="earnings-model-economic-card-explanation">
                {current.explanation}
              </p>
            )}
            {current && (
              <div className="earnings-model-economic-card-meta">
                <span className={`earnings-model-economic-card-meta-item${current.requiresQuota ? ' earnings-model-economic-card-meta-item--yes' : ''}`}>
                  {current.requiresQuota ? '✓ Quota / target required' : '– No quota required'}
                </span>
                <span className={`earnings-model-economic-card-meta-item${current.requiresRateTable ? ' earnings-model-economic-card-meta-item--yes' : ''}`}>
                  {current.requiresRateTable ? '✓ Rate table required' : '– No rate table required'}
                </span>
              </div>
            )}
            {!readOnly && (
              <p className="earnings-model-change-hint">
                To use a different economic model,{' '}
                <span style={{ fontStyle: 'italic' }}>
                  create a new plan
                </span>{' '}
                from the Actions menu with your preferred model.
              </p>
            )}
          </div>
        ) : readOnly ? (
          <>
            <p className="modifier-manager-readonly-note" role="note">
              The earnings model is read-only for this plan version. Changes
              require a successor amendment.
            </p>

            <div className="earnings-model-readonly">
              <p className="earnings-model-readonly-title">
                {current?.label ?? 'No earnings model selected'}
              </p>
              {current && (
                <p className="earnings-model-readonly-copy">
                  {current.description}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <div
              className="earnings-model-grid"
              role="group"
              aria-label="Earnings model options"
            >
              {SPINES.map((spine) => (
                <button
                  key={spine.key}
                  type="button"
                  className="earnings-model-card"
                  data-selected={
                    selectedSpine === spine.key ? 'true' : 'false'
                  }
                  aria-pressed={selectedSpine === spine.key}
                  onClick={() => requestSelection(spine.key)}
                  onKeyDown={(event) =>
                    handleCardKeyDown(event, spine.key)
                  }
                >
                  <span className="earnings-model-card-title">
                    {spine.label}
                  </span>
                  <span className="earnings-model-card-copy">
                    {spine.description}
                  </span>
                </button>
              ))}
            </div>

            {showImpactConfirmation && pending && current && (
              <div
                ref={confirmationRef}
                className="earnings-model-confirmation"
                role="alertdialog"
                aria-modal="false"
                aria-labelledby="earnings-model-impact-title"
                aria-describedby="earnings-model-impact-description"
                tabIndex={-1}
              >
                <p
                  id="earnings-model-impact-title"
                  className="earnings-model-confirmation-title"
                >
                  Confirm earnings model change
                </p>

                <div
                  id="earnings-model-impact-description"
                  className="earnings-model-confirmation-copy"
                >
                  <p>
                    Changing the earnings model to {pending.label} will clear
                    the current rate table reference, quota semantics, and any
                    spine-dependent configuration.
                  </p>
                  <p>
                    Your other sections (Credit Allocation, Payout
                    Adjustments, Settlement, Assignment Scope) are not
                    affected.
                  </p>
                </div>

                <div className="earnings-model-actions">
                  <button
                    type="button"
                    className="primary-button"
                    onClick={confirmChange}
                  >
                    Change earnings model
                  </button>

                  <button
                    type="button"
                    className="secondary-button"
                    onClick={cancelChange}
                  >
                    Keep {current.label}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

EarningsModelSelector.propTypes = {
  selectedSpine: PropTypes.oneOf([
    'revenue_bookings',
    'transaction_commission',
    'volume_quantity',
    'market_share',
    'grr_nrr_retention',
    'mbo_objective',
    'margin_profit',
    'bonus_pool',
    'pharma_script',
  ]),
  hasExistingConfig: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  // S812 D4: true = draft authoring mode — spine locked, show Economic Model card.
  // false (default) = pre-creation mode (NewPlanCreationSheet) — show full 9-card grid.
  isAuthoring: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};
